<template>
  <v-app id="app">
    <AppBar :loading="loading" />

    <transition name="fadeRouter" mode="out-in">
      <v-main :style="appStyles">
        <transition name="fadeRouter" mode="out-in">
          <router-view />
        </transition>
      </v-main>
    </transition>

    <SessionExpiredDialog />
    <InvalidHashDialog />
    <HelpDialog />
    <IssueDialog />
    <ReloadDialog />
    <AzureLoginErrorDialog
      ref="azureLoginErrorDialogRef"
      @hide="closeAzureLoginErrorDialog"
    />
  </v-app>
</template>

<script>
import configs from "../src/configs.js";
import pkg from "../package.json";
import { mapActions, mapMutations, mapGetters } from "vuex";
import AppBar from "@/components/AppBar";
import SessionExpiredDialog from "@/components/znap/SessionExpiredDialog";
import InvalidHashDialog from "@/components/znap/InvalidHashDialog";
import HelpDialog from "@/components/HelpDialog";
import IssueDialog from "@/components/IssueDialog.vue";
import ReloadDialog from "@/components/ReloadDialog.vue";
import AzureLoginErrorDialog from "@/components/Login/AzureLoginErrorDialog";

const version = pkg.version;

export default {
  name: "App",

  components: {
    AppBar,
    SessionExpiredDialog,
    InvalidHashDialog,
    HelpDialog,
    IssueDialog,
    ReloadDialog,
    AzureLoginErrorDialog,
  },

  data() {
    return {
      sn: false,
      payload: {},
      background: "",
      drawerColor: "",
      loading: false,
      appVersion: version,
    };
  },

  computed: {
    ...mapGetters(["getSystem"]),
    ...mapGetters("theme", ["getTheme", "getBg"]),
    ...mapGetters("auth", ["getHash", "getTokenExp"]),

    bgUrl() {
      return `linear-gradient(90deg, ${this.$vuetify.theme.themes.light.primary} 0%, ${this.$vuetify.theme.themes.light.secondary} 100%)`;
    },

    appStyles() {
      return {
        background: `${this.$vuetify.theme.themes.light.primary}`,
        "background-image": `${this.bgUrl}`,
        "background-position": "center",
        "background-repeat": "no-repeat",
        "background-size": "cover",
        "-webkit-background-size": "cover",
        "-moz-background-size": "cover",
        "-o-background-size": "cover",
      };
    },
  },

  methods: {
    ...mapMutations("auth", [
      "setSessionExpiredDialog",
      "setEnableSessionExpiredDialog",
      "setInvalidHashDialog",
      "setUrlPathName",
      "setReloadDialog",
    ]),
    ...mapActions("auth", ["getHashAuth", "hashAuthenticate", "logout"]),
    ...mapActions("theme", ["randomBg"]),

    showAzureLoginErrorDialog(errorData) {
      this.$refs.azureLoginErrorDialogRef.show(errorData);
    },

    closeAzureLoginErrorDialog() {
      this.$refs.azureLoginErrorDialogRef.hide();
    },

    setTheme() {
      const colours = require(`@/plugins/themes/${this.getTheme}`);
      this.$vuetify.theme.themes.light = colours.default;
    },

    async isValidHash(hash) {
      try {
        const res = await this.$http.post(this.$ipUser + "user/validate-hash", {
          ...hash,
        });
        if (res) {
          return !!res.data.isValidHash;
        }
      } catch (err) {
        this.$fnError(err);
        return false;
      }
    },

    isValidToken(tokenExp) {
      let now = Math.floor(Date.now() / 1000);
      return now - tokenExp <= 0;
    },

    removeHashFromURL() {
      window.history.replaceState({}, document.title, "" + "/");
    },

    successLogin(route = null) {
      if (route) {
        let path = `/${route}`;
        this.$router.push(path);
      }

      this.setEnableSessionExpiredDialog(true);
      this.removeHashFromURL();
      this.loading = false;
    },

    async verifyAppVersion() {
      if (!window.location.href.includes(":80")) {
        let serverVersion = "";

        let url = `https://venturus.${configs.modulePath}.znaptech.com/version.json`;
        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            serverVersion = data.version;
            if (this.appVersion !== serverVersion) {
              console.log("serverVersion: ", serverVersion);
              console.log("appVersion: ", this.appVersion);
              return this.setReloadDialog(true);
            } else {
              return this.setReloadDialog(false);
            }
          })
          .catch((err) => {
            this.$fnError(err);
            this.setReloadDialog(false);
          });
      }
    },
  },

  async created() {
    this.setTheme();

    if (this.getBg > 0) {
      this.randomBg();
    }

    this.loading = true;

    this.verifyAppVersion();

    this.setEnableSessionExpiredDialog(false);
    this.setSessionExpiredDialog(false);
    let hash = null;
    let url = new URL(window.location.href);
    let hashUrl = false;
    let hashStore = false;
    let isValidSessionToken = false;
    let isValidHash = false;
    if (url.pathname !== "/") this.setUrlPathName(url.pathname);

    const errorCode = !!url.searchParams.get("errorCode");

    if (errorCode) {
      this.$nextTick(() => {
        return this.showAzureLoginErrorDialog({
          code: errorCode,
          message: url.searchParams.get("errorMessage"),
        });
      });
    }

    hashUrl = !!url.searchParams.get("token");

    if (hashUrl) {
      hash = url.searchParams.get("token");
    } else {
      hash = this.getHash;

      if (hash) {
        hashStore = true;
      }
    }

    if (hash) {
      isValidHash = await this.isValidHash({ hash });
    }

    let tokenExp = this.getTokenExp;
    if (tokenExp) {
      isValidSessionToken = this.isValidToken(tokenExp);
    }

    if (!hashUrl && !hashStore && !isValidSessionToken && !isValidHash) {
      this.loading = false;
      return this.setInvalidHashDialog(true);
    }

    if (hashUrl && isValidHash) {
      const auth = await this.hashAuthenticate(hash);
      if (auth === "success") {
        let route = url.searchParams.get("route");
        this.setInvalidHashDialog(false);
        this.setEnableSessionExpiredDialog(true);
        return this.successLogin(route);
      } else {
        let err = auth;
        this.loading = false;
        this.$fnError(err);
      }
    }

    if (hashStore && isValidHash && !isValidSessionToken) {
      this.loading = false;
      return this.setSessionExpiredDialog(true);
    }

    if ((hashUrl || hashStore) && !isValidHash && !isValidSessionToken) {
      this.loading = false;
      return this.setInvalidHashDialog(true);
    }

    if (!isValidHash && isValidSessionToken) {
      const auth = await this.getHashAuth();
      if (auth === "success") {
        let route = url.searchParams.get("route");
        return this.successLogin(route);
      } else if (auth === "login") {
        this.loading = false;
        return this.setInvalidHashDialog(true);
      } else {
        let err = auth;
        this.$fnError(err);
        this.loading = false;
        return;
      }
    }

    this.loading = false;
    this.setEnableSessionExpiredDialog(true);
  },
};
</script>

<style>
html {
  overflow-y: auto !important;
}

.fadeRouter-enter {
  opacity: 0;
}

.fadeRouter-enter-active {
  transition: opacity 0.28s ease;
}

.fadeRouter-leave-active {
  transition: opacity 0.28s ease;
  opacity: 0;
}

@font-face {
  font-family: "Panton";
  font-weight: 100;
  src: url("./assets/fonts/Panton-Light.otf");
}

@font-face {
  font-family: "Panton";
  font-weight: 400;
  src: url("./assets/fonts/Panton.otf");
}

@font-face {
  font-family: "Panton";
  font-weight: 500;
  src: url("./assets/fonts/Panton-Bold.otf");
}

@font-face {
  font-family: "Panton";
  font-weight: 900;
  src: url("./assets/fonts/Panton-Black.otf");
}

.font-smaller {
  font-size: 10px !important;
}

.font-small {
  font-size: 12px !important;
}

.font-normal {
  font-size: 14px !important;
}

.font-big {
  font-size: 16px !important;
}

.font-bigger {
  font-size: 18px !important;
}

.font-bold {
  font-weight: bold !important;
}

.font-light {
  font-weight: lighter !important;
}

.font-italic {
  font-style: italic;
}

.font-underline {
  text-decoration: underline;
}

/* COLORS */
.bordeaux-1 {
  background-color: #efbfbf !important;
}

.bordeaux-2 {
  background-color: #df7f7f !important;
}

.bordeaux-3 {
  background-color: #d04040 !important;
}

.bordeaux-4 {
  background-color: #c00000 !important;
}

.red-1 {
  background-color: #ffbfbf !important;
}

.red-2 {
  background-color: #ff7f7f !important;
}

.red-3 {
  background-color: #ff4040 !important;
}

.red-4 {
  background-color: #ff0000 !important;
}

.gold-1 {
  background-color: #ffefbf !important;
}

.gold-2 {
  background-color: #ffdf7f !important;
}

.gold-3 {
  background-color: #ffd040 !important;
}

.gold-4 {
  background-color: #ffc000 !important;
}

.yellow-1 {
  background-color: #ffffbf !important;
}

.yellow-2 {
  background-color: #ffff7f !important;
}

.yellow-3 {
  background-color: #ffff40 !important;
}

.yellow-4 {
  background-color: #ffff00 !important;
}

.lime-1 {
  background-color: #e4f3d3 !important;
}

.lime-2 {
  background-color: #c8e7a7 !important;
}

.lime-3 {
  background-color: #addc7c !important;
}

.lime-4 {
  background-color: #92d050 !important;
}

.green-1 {
  background-color: #bfebd3 !important;
}

.green-2 {
  background-color: #7fd7a7 !important;
}

.green-3 {
  background-color: #40c47c !important;
}

.green-4 {
  background-color: #00b050 !important;
}

.blue-1 {
  background-color: #bfebfb !important;
}

.blue-2 {
  background-color: #7fd7f7 !important;
}

.blue-3 {
  background-color: #40c4f4 !important;
}

.blue-4 {
  background-color: #00b0f0 !important;
}

.royal-1 {
  background-color: #bfdbef !important;
}

.royal-2 {
  background-color: #7fb7df !important;
}

.royal-3 {
  background-color: #4094d0 !important;
}

.royal-4 {
  background-color: #0070c0 !important;
}

.navy-1 {
  background-color: #bfc7d7 !important;
}

.navy-2 {
  background-color: #7f8faf !important;
}

.navy-3 {
  background-color: #405888 !important;
}

.navy-4 {
  background-color: #002060 !important;
}

.purple-1 {
  background-color: #dbcbe7 !important;
}

.purple-2 {
  background-color: #b797cf !important;
}

.purple-3 {
  background-color: #9464b8 !important;
}

.purple-4 {
  background-color: #7030a0 !important;
}

.silver-1 {
  background-color: #e8e8e8 !important;
}

.silver-2 {
  background-color: #d2d2d2 !important;
}

.silver-3 {
  background-color: #bbbbbb !important;
}

.silver-4 {
  background-color: #a5a5a5 !important;
}

/* --- Total column style --- */
.total-column {
  background-color: var(--primary-super-light-color) !important;
}

.border-sides-1 {
  box-sizing: border-box !important;
  border-left: 1px solid var(--header-border-color) !important;
  border-right: 1px solid var(--header-border-color) !important;
}

.border-sides-2 {
  box-sizing: border-box !important;
  border-left: 2px solid var(--header-border-color) !important;
  border-right: 2px solid var(--header-border-color) !important;
}

/* --- BORDERS --- */
.border-all-1 {
  box-sizing: border-box !important;
  border-bottom: 1px solid !important;
  border-left: 1px solid !important;
  border-top: 1px solid !important;
}

.border-all-2 {
  box-sizing: border-box !important;
  border-bottom: 2px solid !important;
  border-left: 2px solid !important;
  border-top: 2px solid !important;
}

.border-all-4 {
  box-sizing: border-box !important;
  border-bottom: 4px solid !important;
  border-left: 4px solid !important;
  border-top: 4px solid !important;
}

.border-all-8 {
  box-sizing: border-box !important;
  border-bottom: 8px solid !important;
  border-left: 8px solid !important;
  border-top: 8px solid !important;
}

.border-sides-1 {
  box-sizing: border-box !important;
  border-left: 1px solid !important;
  border-right: 1px solid !important;
}

.border-sides-2 {
  box-sizing: border-box !important;
  border-left: 2px solid !important;
  border-right: 2px solid !important;
}

.border-bottom-1 {
  box-sizing: border-box !important;
  border-bottom: 1px solid !important;
}

.border-bottom-2 {
  box-sizing: border-box !important;
  border-bottom: 2px solid !important;
}

.border-bottom-4 {
  box-sizing: border-box !important;
  border-bottom: 4px solid !important;
}

.border-bottom-8 {
  box-sizing: border-box !important;
  border-bottom: 8px solid !important;
}

.border-top-1 {
  box-sizing: border-box !important;
  border-top: 1px solid !important;
}

.border-top-2 {
  box-sizing: border-box !important;
  border-top: 2px solid !important;
}

.border-top-4 {
  box-sizing: border-box !important;
  border-top: 4px solid !important;
}

.border-top-8 {
  box-sizing: border-box !important;
  border-top: 8px solid !important;
}

.border-black-top {
  border-top-color: black !important;
}

.border-black-bottom {
  border-bottom-color: black !important;
}

.border-gray-top {
  border-top-color: #f1f1f1 !important;
}

.border-gray-bottom {
  border-bottom-color: #f1f1f1 !important;
}

.border-white-top {
  border-top-color: white !important;
}

.border-white-bottom {
  border-bottom-color: white !important;
}

/* READ ONLY COLUMN */
.read-only-column {
  background-color: #f0f0f0 !important;
}

.read-only-row {
  background-color: #f0f0f0 !important;
}
</style>
